import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../lib/supabaseClient';
import {
  FilterPickerModal,
  SpecializationPickerModal,
  EstimatorPickerModal,
  StatusPickerModal,
  DevPickerModal,
  RootPickerModal,
  COMMENT_MAXLEN,
} from './RequirementPickers';
import './RequirementForm.css';

/* ── Icons (mirrors RequirementForm) ─────────────── */
function IconArrowLeft() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

function IconSave() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function IconWarning() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function IconSpinner() {
  return (
    <svg className="rf-spinner-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
      <path d="M12 2a10 10 0 0 1 10 10" />
    </svg>
  );
}

function IconChevronDown() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function IconSuccess() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00CFC1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

/* ── Picker trigger — a button styled like a select that opens a modal ── */
function PickerTrigger({ id, onClick, disabled, placeholder, isEmpty, children }) {
  return (
    <button
      type="button"
      id={id}
      className={`rf-picker-trigger${isEmpty ? ' rf-picker-trigger--empty' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="rf-picker-trigger-label">{isEmpty ? placeholder : children}</span>
      <IconChevronDown />
    </button>
  );
}

/* ── Contrast helper ── */
function getContrastColor(hex) {
  if (!hex) return '#222222';
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#222222' : '#FFFFFF';
}

/* ── Field length limits — kept identical to the table's inline editor
     (`columns` editConfig in Requirements.js) so a duplicate can never produce
     a value the inline editor would later reject. ── */
const MAXLEN = {
  description: 500,
  requirement_type: 30,
  module: 10,
  functional_user_name: 30,
  EF_version: 10,
  EF_file_name: 80,
  billed_percentage: 30,
  comment: COMMENT_MAXLEN,
};

/* ── Stable serialization for the dirty-check ──
   Sorts `developers` so toggling a dev off then on (which reorders the array)
   doesn't falsely register as a change. */
function serializeForm(f) {
  return JSON.stringify({ ...f, developers: [...f.developers].sort() });
}

/* ── Build the initial form from a source requirement row ──
   Copies every meaningful field. Deliberately omits the non-repeatable /
   server-owned columns: id, created_at, updated_at, docs_hyperlink. The folio
   (reference_id) IS copied (usually a near-identical name) but must be changed
   to a unique value before saving. */
function buildInitialForm(src) {
  return {
    client_id: src.project?.client_id != null ? String(src.project.client_id) : '',
    project_id: src.project_id != null ? String(src.project_id) : '',
    reference_id: src.reference_id ?? '',
    description: src.description ?? '',
    specialization_id: src.specialization_id != null ? String(src.specialization_id) : '',
    requirement_type: src.requirement_type ?? '',
    billing_type_id: src.billing_type_id != null ? String(src.billing_type_id) : '',
    module: src.module ?? '',
    status_id: src.status_id != null ? String(src.status_id) : '',
    status_client_id: src.status_client_id != null ? String(src.status_client_id) : '',
    estimator_user_id: src.estimator_user_id ?? '',
    leader_user_id: src.leader_user_id ?? '',
    functional_user_name: src.functional_user_name ?? '',
    estimated_completion_hours_dev: src.estimated_completion_hours_dev != null ? String(src.estimated_completion_hours_dev) : '',
    estimated_completion_hours_client: src.estimated_completion_hours_client != null ? String(src.estimated_completion_hours_client) : '',
    progress_percentage: src.progress_percentage != null ? String(src.progress_percentage) : '',
    billed_percentage: src.billed_percentage ?? '',
    EF_version: src.EF_version ?? '',
    EF_file_name: src.EF_file_name ?? '',
    VoBo_estimated_date: src.VoBo_estimated_date ?? '',
    construction_start_date: src.construction_start_date ?? '',
    dev_delivery_date: src.dev_delivery_date ?? '',
    client_delivery_date: src.client_delivery_date ?? '',
    allow_extended_hours: !!src.allow_extended_hours,
    comment: src.comment ?? '',
    root_requirement_id: src.root_requirement_id ?? '',
    developers: (src.requirement_devs || []).map((d) => String(d.user_id)),
  };
}

/* ── Component ─────────────────────────────────── */
function RequirementDuplicateForm({ source, onBack, onCreated }) {
  /* form data — built synchronously from the source so fields are prefilled
     immediately, then reconciled against the live catalogs once they load */
  const [form, setForm] = useState(() => buildInitialForm(source));
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  /* notices for prefilled values that had to be dropped (inactive/removed) */
  const [droppedNotices, setDroppedNotices] = useState([]);
  /* partial-success: requirement created but dev assignment failed */
  const [partial, setPartial] = useState(false);

  /* dirty snapshot — set after reconciliation so reconciliation itself doesn't
     count as a user edit (cancel only warns on real changes) */
  const initialSnapshot = useRef(null);

  /* catalog data */
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [assignableDevs, setAssignableDevs] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [billingTypes, setBillingTypes] = useState([]);
  const [originals, setOriginals] = useState([]);
  const [existingFolios, setExistingFolios] = useState(() => new Set());
  const [catalogLoading, setCatalogLoading] = useState(true);

  /* which picker modal is open: 'client' | 'project' | 'specialization' |
     'billing_type' | 'status' | 'status_client' | 'estimator' | 'leader' | 'root' | 'devs' | null */
  const [picker, setPicker] = useState(null);

  /* ── Fetch catalogs + reconcile prefilled values ── */
  useEffect(() => {
    let cancelled = false;
    async function fetchCatalogs() {
      setCatalogLoading(true);
      const [clientsRes, projectsRes, usersRes, statusRes, specRes, billingTypeRes, origRes, folioRes] = await Promise.all([
        supabase.from('clients').select('id, name').eq('active', true).order('name'),
        supabase.from('projects').select('id, name, client_id').eq('active', true).order('name'),
        supabase.from('users').select('id, full_name, roles(name)').eq('active', true).order('full_name'),
        supabase.from('status').select('id, name, color').order('name'),
        supabase.from('specializations').select('id, name').order('name'),
        supabase.from('billing_types').select('id, name').eq('active', true).order('name'),
        supabase.from('requirements').select('id, reference_id, description').is('root_requirement_id', null),
        supabase.from('requirements').select('reference_id'),
      ]);
      if (cancelled) return;

      const clientList = clientsRes.data || [];
      const projectList = projectsRes.data || [];
      const userList = usersRes.data || [];
      const statusList = statusRes.data || [];
      const specList = specRes.data || [];
      const billingTypeList = billingTypeRes.data || [];
      const origList = (origRes.data || []).filter((r) => r.id !== source.id);
      const folioSet = new Set((folioRes.data || []).map((r) => r.reference_id));

      const liderList = userList.filter((u) => u.roles?.name === 'Líder' || u.roles?.name === 'Gerente');
      const devList = userList.filter((u) => u.roles?.name === 'Líder' || u.roles?.name === 'Desarrollador');

      setClients(clientList);
      setProjects(projectList);
      setAllUsers(userList);
      setLeaders(liderList);
      setAssignableDevs(devList);
      setStatuses(statusList);
      setSpecializations(specList);
      setBillingTypes(billingTypeList);
      setOriginals(origList);
      setExistingFolios(folioSet);

      /* ── Reconcile prefilled FKs against what's actually selectable now ──
         Anything that's inactive/removed is dropped AND surfaced, so the user
         never believes a value copied when it silently didn't. Computed
         deterministically from the source (the form is a non-editable skeleton
         until this runs, so nothing the user typed can be lost). */
      const notices = [];
      const next = buildInitialForm(source);

      if (next.client_id && !clientList.some((c) => String(c.id) === next.client_id)) {
        notices.push('El cliente original ya no está disponible — selecciónalo de nuevo.');
        next.client_id = '';
        next.project_id = '';
      }
      if (next.project_id && !projectList.some((p) => String(p.id) === next.project_id && String(p.client_id) === next.client_id)) {
        notices.push('El proyecto original ya no está disponible — selecciónalo de nuevo.');
        next.project_id = '';
      }
      if (next.leader_user_id && !liderList.some((u) => String(u.id) === String(next.leader_user_id))) {
        notices.push('El líder original ya no está disponible — selecciónalo de nuevo.');
        next.leader_user_id = '';
      }
      if (next.estimator_user_id && !userList.some((u) => String(u.id) === String(next.estimator_user_id))) {
        notices.push('El estimador original está inactivo — quedó sin asignar.');
        next.estimator_user_id = '';
      }
      if (next.specialization_id && !specList.some((s) => String(s.id) === next.specialization_id)) {
        notices.push('La especialidad original ya no existe — quedó sin asignar.');
        next.specialization_id = '';
      }
      if (next.billing_type_id && !billingTypeList.some((b) => String(b.id) === next.billing_type_id)) {
        notices.push('El tipo de facturación original está inactivo — quedó sin asignar.');
        next.billing_type_id = '';
      }
      if (next.root_requirement_id && !origList.some((r) => String(r.id) === String(next.root_requirement_id))) {
        notices.push('El folio raíz original ya no está disponible — quedó sin raíz.');
        next.root_requirement_id = '';
      }
      const validDevIds = new Set(devList.map((u) => String(u.id)));
      const keptDevs = next.developers.filter((id) => validDevIds.has(id));
      if (keptDevs.length !== next.developers.length) {
        notices.push('Uno o más desarrolladores originales están inactivos — se omitieron.');
        next.developers = keptDevs;
      }

      initialSnapshot.current = serializeForm(next);
      setForm(next);
      setDroppedNotices(notices);
      setCatalogLoading(false);
    }
    fetchCatalogs();
    return () => { cancelled = true; };
  }, [source]);

  /* ── Filtered projects by selected client ── */
  const filteredProjects = useMemo(() => {
    if (!form.client_id) return [];
    return projects.filter((p) => String(p.client_id) === String(form.client_id));
  }, [form.client_id, projects]);

  /* ── Field changes ── */
  const handleClientChange = useCallback((clientId) => {
    setForm((prev) => ({ ...prev, client_id: clientId, project_id: '' }));
    setErrors((prev) => ({ ...prev, client_id: '', project_id: '' }));
  }, []);

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    if (submitError) setSubmitError('');
  }

  /* ── Validation ── */
  function validate() {
    const errs = {};
    if (!form.client_id) errs.client_id = 'Selecciona un cliente';
    if (!form.project_id) errs.project_id = 'Selecciona un proyecto';

    const folio = form.reference_id.trim();
    if (!folio) {
      errs.reference_id = 'Ingresa el ID Folio';
    } else if (existingFolios.has(folio)) {
      errs.reference_id = 'El ID Folio ya existe. Ingresa uno diferente.';
    }

    if (!form.description.trim()) errs.description = 'Ingresa una descripción';
    if (!form.leader_user_id) errs.leader_user_id = 'Selecciona un líder';
    if (!form.status_id) errs.status_id = 'Selecciona un estatus';

    for (const f of ['estimated_completion_hours_dev', 'estimated_completion_hours_client']) {
      const v = form[f];
      if (v !== '') {
        const n = Number(v);
        if (isNaN(n) || n < 0) errs[f] = 'Valor inválido';
        else if (Math.abs(n * 2 - Math.round(n * 2)) > 1e-9) errs[f] = 'Debe ser múltiplo de 0.5';
      }
    }
    if (form.progress_percentage !== '') {
      const n = parseInt(form.progress_percentage, 10);
      if (isNaN(n) || n < 0 || n > 100) errs.progress_percentage = 'Debe estar entre 0 y 100';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  /* ── Submit ── */
  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setSubmitError('');

    const payload = {
      project_id: Number(form.project_id),
      reference_id: form.reference_id.trim(),
      description: form.description.trim(),
      status_id: Number(form.status_id),
      leader_user_id: form.leader_user_id,
      allow_extended_hours: !!form.allow_extended_hours,
      status_client_id: form.status_client_id ? Number(form.status_client_id) : null,
      specialization_id: form.specialization_id ? Number(form.specialization_id) : null,
      estimator_user_id: form.estimator_user_id || null,
      requirement_type: form.requirement_type.trim() || null,
      billing_type_id: form.billing_type_id ? Number(form.billing_type_id) : null,
      module: form.module.trim() || null,
      functional_user_name: form.functional_user_name.trim() || null,
      estimated_completion_hours_dev: form.estimated_completion_hours_dev !== '' ? Number(form.estimated_completion_hours_dev) : null,
      estimated_completion_hours_client: form.estimated_completion_hours_client !== '' ? Number(form.estimated_completion_hours_client) : null,
      progress_percentage: form.progress_percentage !== '' ? parseInt(form.progress_percentage, 10) : null,
      billed_percentage: form.billed_percentage.trim() || null,
      EF_version: form.EF_version.trim() || null,
      EF_file_name: form.EF_file_name.trim() || null,
      VoBo_estimated_date: form.VoBo_estimated_date || null,
      construction_start_date: form.construction_start_date || null,
      dev_delivery_date: form.dev_delivery_date || null,
      client_delivery_date: form.client_delivery_date || null,
      comment: form.comment.trim() || null,
      root_requirement_id: form.root_requirement_id || null,
      /* NOT copied: id / created_at / updated_at (server-set) and
         docs_hyperlink (a fresh SharePoint folder is created below). */
    };

    const { data: inserted, error } = await supabase
      .from('requirements')
      .insert([payload])
      .select('id')
      .single();

    if (error || !inserted?.id) {
      setSaving(false);
      if (error?.code === '23505' && error.message?.includes('reference_id')) {
        setSubmitError('El ID Folio ya existe. Ingresa uno diferente.');
        setErrors((prev) => ({ ...prev, reference_id: 'Folio duplicado' }));
        setExistingFolios((prev) => new Set(prev).add(payload.reference_id));
      } else {
        setSubmitError(error?.message || 'Error al crear el requerimiento.');
      }
      return;
    }

    const newId = inserted.id;

    /* ── PA flows ──────────────────────────────────────────────────────────
       A duplicate fires BOTH the create-time flow AND the chosen "modify"
       flows (mirroring what would fire if you created a minimal requirement
       then edited these values in). All fire-and-forget; every notify-* EF
       no-ops if its slug is inactive.

       Per product decision, a duplicate fires:
         • create-requirement-folder  → fresh SharePoint folder + estimator
           notification (so estimator is NEVER double-sent — we do not call
           notify-estimator-assigned here).
         • notify-devs-assigned       → if developers were assigned.
         • notify-internal-status-change → always (status is required).
         • notify-comment-update      → if a comment is present.
       Client-status notification is intentionally NOT fired.

       The status/comment EFs build their recipient list from the requirement's
       leader + estimator + assigned devs, so they must run AFTER the devs are
       inserted to reach everyone. ───────────────────────────────────────── */

    /* create-time flow: fresh SharePoint folder + estimator notification */
    supabase.functions.invoke('create-requirement-folder', {
      body: {
        reference_id: payload.reference_id,
        requirement_id: newId,
        estimator_user_id: payload.estimator_user_id || null,
        leader_user_id: payload.leader_user_id,
      },
    }).catch((err) => console.error('create-requirement-folder failed:', err));

    /* modify flows (status + comment) — fired after devs are in place so the
       assigned developers are included as recipients */
    const fireStatusAndComment = () => {
      const newStatusName = statuses.find((s) => String(s.id) === String(form.status_id))?.name || '';
      supabase.functions.invoke('notify-internal-status-change', {
        body: { requirement_id: newId, old_status_name: '', new_status_name: newStatusName },
      }).catch(() => {});

      const newComment = form.comment.trim();
      if (newComment) {
        supabase.functions.invoke('notify-comment-update', {
          body: { requirement_id: newId, old_comment: null, new_comment: newComment },
        }).catch(() => {});
      }
    };

    /* assign developers, then fire dev + status + comment notifications */
    if (form.developers.length > 0) {
      const { error: devErr } = await supabase
        .from('requirement_devs')
        .insert(form.developers.map((uid) => ({ requirement_id: newId, user_id: uid })));
      if (devErr) {
        /* requirement already exists and can't be cleanly rolled back (Líderes
           cannot delete) — keep it, notify the team (sans devs), and tell the
           user to assign developers manually */
        fireStatusAndComment();
        setSaving(false);
        setPartial(true);
        return;
      }
      supabase.functions.invoke('notify-devs-assigned', {
        body: { requirement_id: newId, new_user_ids: form.developers },
      }).catch(() => {});
    }

    fireStatusAndComment();

    onCreated?.();
    onBack();
  }

  function handleContinueAfterPartial() {
    onCreated?.();
    onBack();
  }

  /* ── Cancel flow (only warns on real changes) ── */
  function handleCancelClick() {
    const dirty = initialSnapshot.current != null && serializeForm(form) !== initialSnapshot.current;
    if (dirty) setShowCancelModal(true);
    else onBack();
  }

  function confirmCancel() {
    setShowCancelModal(false);
    onBack();
  }

  const selectedStatus = statuses.find((s) => String(s.id) === String(form.status_id));
  const selectedClientStatus = statuses.find((s) => String(s.id) === String(form.status_client_id));
  const selectedClient = clients.find((c) => String(c.id) === String(form.client_id));
  const selectedProject = filteredProjects.find((p) => String(p.id) === String(form.project_id));
  const selectedSpec = specializations.find((s) => String(s.id) === String(form.specialization_id));
  const selectedBillingType = billingTypes.find((b) => String(b.id) === String(form.billing_type_id));
  const selectedLeader = leaders.find((u) => String(u.id) === String(form.leader_user_id));
  const selectedEstimator = allUsers.find((u) => String(u.id) === String(form.estimator_user_id));
  const selectedRoot = originals.find((r) => String(r.id) === String(form.root_requirement_id));
  const selectedDevNames = form.developers
    .map((id) => assignableDevs.find((u) => String(u.id) === id)?.full_name)
    .filter(Boolean);

  /* ── Skeleton for loading ── */
  if (catalogLoading) {
    return (
      <div className="rf-container">
        <div className="rf-header">
          <div className="rf-skeleton rf-skeleton--title" />
        </div>
        <div className="rf-form-skeleton">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rf-skeleton rf-skeleton--field" />
          ))}
        </div>
      </div>
    );
  }

  /* ── Partial success screen ── */
  if (partial) {
    return (
      <div className="rf-container">
        <div className="rf-header">
          <h1 className="rf-title">Requerimiento creado</h1>
        </div>
        <div className="rf-submit-error rf-field--full" style={{ marginBottom: 16 }}>
          <IconWarning />
          <span>
            El requerimiento <strong>{form.reference_id.trim()}</strong> se creó correctamente, pero no se
            pudieron asignar los desarrolladores. Puedes asignarlos desde la tabla con “Editar Campos”.
          </span>
        </div>
        <div className="rf-actions rf-field--full">
          <button type="button" className="rf-btn rf-btn--submit" onClick={handleContinueAfterPartial}>
            <IconSave /><span>Ir a la tabla</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rf-container">
      {/* ── Header ── */}
      <div className="rf-header">
        <button className="rf-back-btn" onClick={handleCancelClick} type="button">
          <IconArrowLeft />
          <span>Regresar</span>
        </button>
        <h1 className="rf-title">Duplicar Requerimiento</h1>
      </div>

      {/* ── Source / reconciliation notices ── */}
      <div className="rf-dup-banner">
        <IconSuccess />
        <span>
          Duplicando <strong>{source.reference_id}</strong>. Todos los campos están precargados — modifica lo
          que cambie. El <strong>ID Folio</strong> debe ser único y se creará una carpeta de SharePoint nueva.
        </span>
      </div>
      {droppedNotices.length > 0 && (
        <div className="rf-notice">
          <IconWarning />
          <ul className="rf-notice-list">
            {droppedNotices.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </div>
      )}

      {/* ── Form ── */}
      <form className="rf-form" onSubmit={handleSubmit} noValidate>
        {/* Cliente */}
        <div className={`rf-field${errors.client_id ? ' rf-field--error' : ''}`}>
          <label className="rf-label" htmlFor="rdf-client">Cliente <span className="rf-required">*</span></label>
          <PickerTrigger id="rdf-client" onClick={() => setPicker('client')} isEmpty={!selectedClient} placeholder="— Seleccionar cliente —">
            {selectedClient?.name}
          </PickerTrigger>
          {errors.client_id && <span className="rf-error-msg">{errors.client_id}</span>}
        </div>

        {/* Proyecto */}
        <div className={`rf-field${errors.project_id ? ' rf-field--error' : ''}`}>
          <label className="rf-label" htmlFor="rdf-project">Proyecto <span className="rf-required">*</span></label>
          <PickerTrigger
            id="rdf-project"
            onClick={() => setPicker('project')}
            disabled={!form.client_id}
            isEmpty={!selectedProject}
            placeholder={form.client_id ? '— Seleccionar proyecto —' : '— Selecciona un cliente primero —'}
          >
            {selectedProject?.name}
          </PickerTrigger>
          {errors.project_id && <span className="rf-error-msg">{errors.project_id}</span>}
        </div>

        {/* ID Folio */}
        <div className={`rf-field${errors.reference_id ? ' rf-field--error' : ''}`}>
          <label className="rf-label" htmlFor="rdf-folio">ID Folio <span className="rf-required">*</span></label>
          <input id="rdf-folio" type="text" className="rf-input" placeholder="Ej. WSP-REQ-002" value={form.reference_id} onChange={(e) => handleChange('reference_id', e.target.value)} />
          {errors.reference_id && <span className="rf-error-msg">{errors.reference_id}</span>}
        </div>

        {/* Especialidad */}
        <div className="rf-field">
          <label className="rf-label" htmlFor="rdf-spec">Especialidad <span className="rf-optional">(opcional)</span></label>
          <PickerTrigger id="rdf-spec" onClick={() => setPicker('specialization')} isEmpty={!selectedSpec} placeholder="— Sin especialidad —">
            {selectedSpec?.name}
          </PickerTrigger>
        </div>

        {/* Descripción */}
        <div className={`rf-field rf-field--full${errors.description ? ' rf-field--error' : ''}`}>
          <label className="rf-label" htmlFor="rdf-desc">Descripción <span className="rf-required">*</span></label>
          <textarea id="rdf-desc" className="rf-textarea" rows="3" maxLength={MAXLEN.description} placeholder="Describe el requerimiento…" value={form.description} onChange={(e) => handleChange('description', e.target.value)} />
          {errors.description && <span className="rf-error-msg">{errors.description}</span>}
        </div>

        {/* Tipo de Requerimiento */}
        <div className="rf-field">
          <label className="rf-label" htmlFor="rdf-type">Tipo de Requerimiento <span className="rf-optional">(opcional)</span></label>
          <input id="rdf-type" type="text" className="rf-input" maxLength={MAXLEN.requirement_type} value={form.requirement_type} onChange={(e) => handleChange('requirement_type', e.target.value)} />
        </div>

        {/* Tipo de Facturación */}
        <div className="rf-field">
          <label className="rf-label" htmlFor="rdf-billing-type">Tipo de Facturación <span className="rf-optional">(opcional)</span></label>
          <PickerTrigger id="rdf-billing-type" onClick={() => setPicker('billing_type')} isEmpty={!selectedBillingType} placeholder="— Sin tipo de facturación —">
            {selectedBillingType?.name}
          </PickerTrigger>
        </div>

        {/* Módulo */}
        <div className="rf-field">
          <label className="rf-label" htmlFor="rdf-module">Módulo <span className="rf-optional">(opcional)</span></label>
          <input id="rdf-module" type="text" className="rf-input" maxLength={MAXLEN.module} value={form.module} onChange={(e) => handleChange('module', e.target.value)} />
        </div>

        {/* Líder */}
        <div className={`rf-field${errors.leader_user_id ? ' rf-field--error' : ''}`}>
          <label className="rf-label" htmlFor="rdf-leader">Líder <span className="rf-required">*</span></label>
          <PickerTrigger id="rdf-leader" onClick={() => setPicker('leader')} isEmpty={!selectedLeader} placeholder="— Seleccionar líder —">
            {selectedLeader?.full_name}
          </PickerTrigger>
          {errors.leader_user_id && <span className="rf-error-msg">{errors.leader_user_id}</span>}
        </div>

        {/* Estimador */}
        <div className="rf-field">
          <label className="rf-label" htmlFor="rdf-estimator">Estimador <span className="rf-optional">(opcional)</span></label>
          <PickerTrigger id="rdf-estimator" onClick={() => setPicker('estimator')} isEmpty={!selectedEstimator} placeholder="— Sin estimador —">
            {selectedEstimator?.full_name}
          </PickerTrigger>
        </div>

        {/* Funcional */}
        <div className="rf-field">
          <label className="rf-label" htmlFor="rdf-functional">Funcional <span className="rf-optional">(opcional)</span></label>
          <input id="rdf-functional" type="text" className="rf-input" maxLength={MAXLEN.functional_user_name} value={form.functional_user_name} onChange={(e) => handleChange('functional_user_name', e.target.value)} />
        </div>

        {/* Estatus */}
        <div className={`rf-field${errors.status_id ? ' rf-field--error' : ''}`}>
          <label className="rf-label" htmlFor="rdf-status">Estatus <span className="rf-required">*</span></label>
          <PickerTrigger id="rdf-status" onClick={() => setPicker('status')} isEmpty={!selectedStatus} placeholder="— Seleccionar estatus —">
            {selectedStatus && (
              <span className="rf-status-preview" style={{ backgroundColor: selectedStatus.color, color: getContrastColor(selectedStatus.color) }}>{selectedStatus.name}</span>
            )}
          </PickerTrigger>
          {errors.status_id && <span className="rf-error-msg">{errors.status_id}</span>}
        </div>

        {/* Estatus Cliente */}
        <div className="rf-field">
          <label className="rf-label" htmlFor="rdf-status-client">Estatus Cliente <span className="rf-optional">(opcional)</span></label>
          <PickerTrigger id="rdf-status-client" onClick={() => setPicker('status_client')} isEmpty={!selectedClientStatus} placeholder="— Sin estatus cliente —">
            {selectedClientStatus && (
              <span className="rf-status-preview" style={{ backgroundColor: selectedClientStatus.color, color: getContrastColor(selectedClientStatus.color) }}>{selectedClientStatus.name}</span>
            )}
          </PickerTrigger>
        </div>

        {/* Horas Estimadas Dev */}
        <div className={`rf-field${errors.estimated_completion_hours_dev ? ' rf-field--error' : ''}`}>
          <label className="rf-label" htmlFor="rdf-hours-dev">Horas Estimadas Dev <span className="rf-optional">(opcional)</span></label>
          <input id="rdf-hours-dev" type="number" min="0" step="0.5" className="rf-input" value={form.estimated_completion_hours_dev} onChange={(e) => handleChange('estimated_completion_hours_dev', e.target.value)} />
          {errors.estimated_completion_hours_dev && <span className="rf-error-msg">{errors.estimated_completion_hours_dev}</span>}
        </div>

        {/* Horas Estimadas Cliente */}
        <div className={`rf-field${errors.estimated_completion_hours_client ? ' rf-field--error' : ''}`}>
          <label className="rf-label" htmlFor="rdf-hours-client">Horas Estimadas Cliente <span className="rf-optional">(opcional)</span></label>
          <input id="rdf-hours-client" type="number" min="0" step="0.5" className="rf-input" value={form.estimated_completion_hours_client} onChange={(e) => handleChange('estimated_completion_hours_client', e.target.value)} />
          {errors.estimated_completion_hours_client && <span className="rf-error-msg">{errors.estimated_completion_hours_client}</span>}
        </div>

        {/* % Avance */}
        <div className={`rf-field${errors.progress_percentage ? ' rf-field--error' : ''}`}>
          <label className="rf-label" htmlFor="rdf-progress">% Avance <span className="rf-optional">(opcional)</span></label>
          <input id="rdf-progress" type="number" min="0" max="100" step="1" className="rf-input" value={form.progress_percentage} onChange={(e) => handleChange('progress_percentage', e.target.value)} />
          {errors.progress_percentage && <span className="rf-error-msg">{errors.progress_percentage}</span>}
        </div>

        {/* % Facturado */}
        <div className="rf-field">
          <label className="rf-label" htmlFor="rdf-billed">% Facturado <span className="rf-optional">(opcional)</span></label>
          <input id="rdf-billed" type="text" className="rf-input" maxLength={MAXLEN.billed_percentage} placeholder="Ej. Enero 30%" value={form.billed_percentage} onChange={(e) => handleChange('billed_percentage', e.target.value)} />
        </div>

        {/* Versión EF */}
        <div className="rf-field">
          <label className="rf-label" htmlFor="rdf-efv">Versión EF <span className="rf-optional">(opcional)</span></label>
          <input id="rdf-efv" type="text" className="rf-input" maxLength={MAXLEN.EF_version} value={form.EF_version} onChange={(e) => handleChange('EF_version', e.target.value)} />
        </div>

        {/* Nombre Archivo EF */}
        <div className="rf-field">
          <label className="rf-label" htmlFor="rdf-eff">Nombre Archivo EF <span className="rf-optional">(opcional)</span></label>
          <input id="rdf-eff" type="text" className="rf-input" maxLength={MAXLEN.EF_file_name} value={form.EF_file_name} onChange={(e) => handleChange('EF_file_name', e.target.value)} />
        </div>

        {/* Fecha Estimación VoBo */}
        <div className="rf-field">
          <label className="rf-label" htmlFor="rdf-vobo">Fecha Estimación VoBo <span className="rf-optional">(opcional)</span></label>
          <input id="rdf-vobo" type="date" className="rf-input" value={form.VoBo_estimated_date} onChange={(e) => handleChange('VoBo_estimated_date', e.target.value)} />
        </div>

        {/* Fecha Inicio Construcción */}
        <div className="rf-field">
          <label className="rf-label" htmlFor="rdf-cons">Fecha Inicio Construcción <span className="rf-optional">(opcional)</span></label>
          <input id="rdf-cons" type="date" className="rf-input" value={form.construction_start_date} onChange={(e) => handleChange('construction_start_date', e.target.value)} />
        </div>

        {/* Fecha Entrega Desarrollador */}
        <div className="rf-field">
          <label className="rf-label" htmlFor="rdf-devd">Fecha Entrega Desarrollador <span className="rf-optional">(opcional)</span></label>
          <input id="rdf-devd" type="date" className="rf-input" value={form.dev_delivery_date} onChange={(e) => handleChange('dev_delivery_date', e.target.value)} />
        </div>

        {/* Fecha Entrega Cliente */}
        <div className="rf-field">
          <label className="rf-label" htmlFor="rdf-cld">Fecha Entrega Cliente <span className="rf-optional">(opcional)</span></label>
          <input id="rdf-cld" type="date" className="rf-input" value={form.client_delivery_date} onChange={(e) => handleChange('client_delivery_date', e.target.value)} />
        </div>

        {/* Folio Raíz */}
        <div className="rf-field">
          <label className="rf-label" htmlFor="rdf-root">Folio Raíz <span className="rf-optional">(opcional)</span></label>
          <PickerTrigger id="rdf-root" onClick={() => setPicker('root')} isEmpty={!selectedRoot} placeholder="— Sin raíz —">
            {selectedRoot?.reference_id}
          </PickerTrigger>
        </div>

        {/* Horas Extendidas Autorizadas */}
        <div className="rf-field">
          <label className="rf-label" htmlFor="rdf-ext">Horas Extendidas Autorizadas</label>
          <button
            id="rdf-ext"
            type="button"
            role="switch"
            aria-checked={form.allow_extended_hours}
            className={`rf-toggle${form.allow_extended_hours ? ' rf-toggle--on' : ''}`}
            onClick={() => handleChange('allow_extended_hours', !form.allow_extended_hours)}
          >
            <span className="rf-toggle-knob" />
          </button>
        </div>

        {/* Desarrolladores */}
        <div className="rf-field rf-field--full">
          <label className="rf-label">Desarrolladores <span className="rf-optional">(opcional)</span></label>
          <PickerTrigger
            id="rdf-devs"
            onClick={() => setPicker('devs')}
            isEmpty={form.developers.length === 0}
            placeholder="Asignar desarrolladores"
          >
            {form.developers.length} desarrollador{form.developers.length !== 1 ? 'es' : ''} seleccionado{form.developers.length !== 1 ? 's' : ''}
          </PickerTrigger>
          {selectedDevNames.length > 0 && (
            <div className="rf-chip-row">
              {selectedDevNames.map((name, i) => (
                <span key={i} className="rf-chip">{name}</span>
              ))}
            </div>
          )}
        </div>

        {/* Comentarios */}
        <div className="rf-field rf-field--full">
          <label className="rf-label" htmlFor="rdf-comment">Comentarios <span className="rf-optional">(opcional)</span></label>
          <textarea id="rdf-comment" className="rf-textarea" rows="3" maxLength={MAXLEN.comment} placeholder="Comentarios adicionales…" value={form.comment} onChange={(e) => handleChange('comment', e.target.value)} />
          <span className="rf-char-count">{form.comment.length}/{MAXLEN.comment}</span>
        </div>

        {/* Submit error */}
        {submitError && (
          <div className="rf-submit-error rf-field--full">
            <IconWarning />
            <span>{submitError}</span>
          </div>
        )}

        {/* Actions */}
        <div className="rf-actions rf-field--full">
          <button type="button" className="rf-btn rf-btn--cancel" onClick={handleCancelClick} disabled={saving}>Cancelar</button>
          <button type="submit" className="rf-btn rf-btn--submit" disabled={saving}>
            {saving ? (<><IconSpinner /><span>Guardando…</span></>) : (<><IconSave /><span>Crear Requerimiento</span></>)}
          </button>
        </div>
      </form>

      {/* ── Cancel confirmation modal ── */}
      {showCancelModal && createPortal(
        <div className="rf-modal-backdrop" onClick={() => setShowCancelModal(false)}>
          <div className="rf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rf-modal-icon"><IconWarning /></div>
            <p className="rf-modal-text">Se perderán los cambios, ¿estás seguro de querer cancelar la duplicación?</p>
            <div className="rf-modal-actions">
              <button className="rf-btn rf-btn--modal-back" onClick={() => setShowCancelModal(false)} type="button">No, quiero regresar</button>
              <button className="rf-btn rf-btn--modal-confirm" onClick={confirmCancel} type="button">Sí, quiero cancelar</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Picker modals — portaled to document.body so position:fixed
           resolves to the viewport, not the transformed .req-main ── */}
      {createPortal(
        <>
          <FilterPickerModal
            open={picker === 'client'}
            title="Seleccionar Cliente"
            options={clients}
            value={form.client_id}
            onSelect={(id) => handleClientChange(id ? String(id) : '')}
            onClose={() => setPicker(null)}
            labelKey="name"
            emptyOption={null}
          />
          <FilterPickerModal
            open={picker === 'project'}
            title="Seleccionar Proyecto"
            options={filteredProjects}
            value={form.project_id}
            onSelect={(id) => handleChange('project_id', id ? String(id) : '')}
            onClose={() => setPicker(null)}
            labelKey="name"
            emptyOption={null}
          />
          <SpecializationPickerModal
            open={picker === 'specialization'}
            options={specializations}
            value={form.specialization_id}
            onSelect={(id) => { handleChange('specialization_id', id ? String(id) : ''); setPicker(null); }}
            onClose={() => setPicker(null)}
          />
          <SpecializationPickerModal
            open={picker === 'billing_type'}
            title="Seleccionar Tipo de Facturación"
            emptyLabel="Sin tipo de facturación"
            options={billingTypes}
            value={form.billing_type_id}
            onSelect={(id) => { handleChange('billing_type_id', id ? String(id) : ''); setPicker(null); }}
            onClose={() => setPicker(null)}
          />
          <StatusPickerModal
            open={picker === 'status'}
            options={statuses}
            value={form.status_id}
            onSelect={(id) => { handleChange('status_id', id ? String(id) : ''); setPicker(null); }}
            onClose={() => setPicker(null)}
          />
          <StatusPickerModal
            open={picker === 'status_client'}
            options={statuses}
            value={form.status_client_id}
            emptyLabel="Sin estatus cliente"
            onSelect={(id) => { handleChange('status_client_id', id ? String(id) : ''); setPicker(null); }}
            onClose={() => setPicker(null)}
          />
          <EstimatorPickerModal
            open={picker === 'estimator'}
            options={allUsers}
            value={form.estimator_user_id}
            onSelect={(id) => handleChange('estimator_user_id', id || '')}
            onClose={() => setPicker(null)}
          />
          <EstimatorPickerModal
            title="Seleccionar Líder"
            emptyLabel="Sin líder"
            open={picker === 'leader'}
            options={leaders}
            value={form.leader_user_id}
            onSelect={(id) => handleChange('leader_user_id', id || '')}
            onClose={() => setPicker(null)}
          />
          <RootPickerModal
            open={picker === 'root'}
            options={originals}
            currentId={form.root_requirement_id || null}
            onConfirm={(id) => handleChange('root_requirement_id', id || '')}
            onClose={() => setPicker(null)}
          />
          <DevPickerModal
            open={picker === 'devs'}
            options={assignableDevs}
            selectedIds={form.developers}
            onConfirm={(ids) => setForm((prev) => ({ ...prev, developers: ids.map(String) }))}
            onClose={() => setPicker(null)}
          />
        </>,
        document.body
      )}
    </div>
  );
}

export default RequirementDuplicateForm;
